import React, { useState, useEffect, useRef } from "react";
import {
  Avatar,
  Button,
  Input,
  Form,
  Select,
  Row,
  Col,
  Spin,
  Tooltip,
  message
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  QuestionCircleOutlined,
  LinkOutlined
} from "@ant-design/icons";
import UserIcon from "../../../../assets/images/icons/userLogo.svg";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useStore } from "../../../../store";
import { TAB_KEYS } from "../../../../config";
import { useTab } from "../../../../custom-hooks/useTab";
import { useConnectsuForm } from "../../../../custom-hooks/useConnectsuForm";
import { getFormFieldValue } from "../../../../utils";
import ReactPlayer from "react-player/youtube";
import products from "../../../organisations/json/products.json";
import SynthesiaVideo from "./SynthesiaVideo";

import 'react-quill/dist/quill.snow.css'
import ReactQuill from 'react-quill';

const NewsTab = ({ dataLoader, form, getSingleTabData, loader: formDataLoader }) => {
  const { state } = useLocation();

  const tabKey = state;
  const { tabData } = useStore();
  const whichTab = TAB_KEYS.news;
  const { deleteSingleItem, postLoader } = useTab();
  const { updateImage, updateFile } = useConnectsuForm();
  const [loader, setLoader] = useState(formDataLoader || true);
  const [formChanged, setFormChanged] = useState(false);
  const { updatedValues } = useStore();
  const [items, setItems] = useState(products.map((item) => item.name));

  const [canAddNew, setCanAddNew] = useState(true);
  const [hasUnsavedItem, setHasUnsavedItem] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const ReactQuillModules = {
    toolbar: [
      [{ header: "1" }, { header: "2" }, { font: [] }],
      [{ size: [] }],
      ["bold", "italic", "underline", "strike", "blockquote"],
      [
        { list: "ordered" },
        { list: "bullet" },
        { indent: "-1" },
        { indent: "+1" }
      ],
      ["link", "image"]
    ],
    clipboard: { matchVisual: false }
  };

  // ✅ Empty Row Template
  const emptyRow = {
    id: undefined,
    title: "",
    description: "",
    type: "news-tab",
    media: {},
    urlLink: "",
    mediaLink: { mediaType: undefined, url: "" },
  };

  // ✅ Refresh Unsaved State
  const refreshUnsavedState = () => {
    const currentList = form.getFieldValue(whichTab) || [];
    const unsavedItem = currentList.find(item => {
      const hasData =
        item?.title?.trim() ||
        item?.description?.trim() ||
        item?.urlLink?.trim() ||
        item?.mediaLink?.url?.trim() ||
        item?.mediaLink?.mediaType ||
        item?.media;
      return !item?.id && hasData;
    });

    setCanAddNew(!unsavedItem);
    setHasUnsavedItem(!!unsavedItem);
  };

  // ✅ Add More Handler - NEW ITEMS AT TOP
  const handleAddMore = () => {
    if (!canAddNew) {
      message.warning("You must save or clear the current item before adding a new one.");
      return;
    }

    const currentList = form.getFieldValue(whichTab) || [];
    const newList = [emptyRow, ...currentList]; // Add new row on top
    form.setFieldsValue({ [whichTab]: newList });

    setCanAddNew(false);
    setHasUnsavedItem(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ✅ Clear Handler - CLEAR ONLY THE FIRST (NEWEST) UNSAVED ITEM
  const handleClear = () => {
    const currentList = form.getFieldValue(whichTab) || [];

    // Find the first unsaved item (index 0, since new items are added at top)
    const firstUnsavedIndex = currentList.findIndex(item => !item?.id);

    if (firstUnsavedIndex === -1) {
      message.warning("No unsaved item to clear.");
      return;
    }

    // Clear ONLY the first unsaved item
    const updatedList = currentList.map((item, idx) =>
      idx === firstUnsavedIndex
        ? {
          ...item,
          title: "",
          description: "",
          urlLink: "",
          media: {},
          mediaLink: { mediaType: undefined, url: "" },
        }
        : item
    );

    form.setFieldsValue({ [whichTab]: updatedList });

    // ✅ Recalculate unsaved state
    const stillUnsaved = updatedList.some(item =>
      !item?.id &&
      (
        item?.title?.trim() ||
        item?.description?.trim() ||
        item?.urlLink?.trim() ||
        item?.mediaLink?.url?.trim() ||
        item?.mediaLink?.mediaType
      )
    );

    // setCanAddNew(!stillUnsaved);
    // setHasUnsavedItem(stillUnsaved);

  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (tabData?.news_tab?.length === 0) {
        setHasUnsavedItem(true);
      } else {
        setHasUnsavedItem(false);
      }
    }, 500);

    return () => clearTimeout(timer); // cleanup for safety
  }, [tabData?.news_tab]);


  // ✅ Updated renderMedia
  const renderMedia = (dataItem) => {
    const commonStyles = {
      width: "350px",
      height: "250px",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      backgroundColor: "#000",
    };

    const mediaType = dataItem?.mediaLink?.mediaType;
    const fileBlob = dataItem?.media?.originFileObj || dataItem?.media?.file || dataItem?.blob;
    const uploadedUrl = fileBlob ? URL.createObjectURL(fileBlob) : dataItem?.mediaLink?.url;

    if (!mediaType || !uploadedUrl) return null;

    if (mediaType === "SOCIAL_MEDIA") {
      return (
        <div className="iframe-comp" style={commonStyles}>
          <ReactPlayer controls url={uploadedUrl} width="100%" height="100%" />
        </div>
      );
    }

    if (mediaType === "VIMEO_MEDIA") {
      return (
        <div className="iframe-comp" style={commonStyles}>
          <iframe
            src={uploadedUrl}
            frameBorder="0"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            width="100%"
            height="100%"
          />
        </div>
      );
    }

    if (mediaType === "IMAGE") {
      return (
        <div className="iframe-comp" style={commonStyles}>
          <img
            src={uploadedUrl}
            alt="uploaded media"
            style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
          />
        </div>
      );
    } else if (
      dataItem?.mediaLink?.mediaType === "SYNTHESIA_MEDIA" &&
      dataItem.mediaLink.url) {
      return (
        <div>
          <SynthesiaVideo key={dataItem.id} videoData={dataItem} />

        </div>
      )
    }

    return null;
  };

  const findValue = (i, key) => getFormFieldValue(form, whichTab, i, key);

  const SelectChangeHandler = (e, i) => {
    form.setFieldValue([whichTab, i, "mediaLink", "url"], "");
  };

  // ✅ Handle ReactQuill change
  const handleDescriptionChange = (value, i) => {
    const currentList = form.getFieldValue(whichTab) || [];
    const updatedList = currentList.map((item, index) =>
      index === i ? { ...item, description: value } : item
    );
    form.setFieldValue(whichTab, updatedList);
  };

  useEffect(() => {
    // localStorage.setItem("tab", "NEWS");
    refreshUnsavedState();
  }, []);

  useEffect(() => {
    const isNotEmpty = Object.keys(updatedValues).length > 0;
    setFormChanged(isNotEmpty);
  }, [Object.keys(updatedValues).length]);

  useEffect(() => {
    form.resetFields();
    refreshUnsavedState();
    setTimeout(() => setLoader(false), 1000);
  }, [tabData[whichTab]]);

  // ✅ Subscribe to form changes for unsaved state
  useEffect(() => {
    const unsubscribe = form.subscribe?.(() => refreshUnsavedState());
    return () => unsubscribe?.();
  }, [form]);


  if (dataLoader || postLoader || loader) {
    return <div className="data-loader">
      <Spin tip="Loading" size="large" />
    </div>
  } else {
    return <div>
      <Row className="rowBody" gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
        {tabKey === whichTab ? (
          <Col className="gutter-row tabContent" span={24}>
            <Form.List
              name={whichTab}
              initialValue={
                tabData &&
                  tabData[whichTab] &&
                  tabData[whichTab].length > 0
                  ? tabData[whichTab]
                  : [{
                    "title": "",
                    "description": "",
                    "type": "news-tab",
                    "media": {},
                    "urlLink": ''
                  }]
              }
            >
              {(fields, { add, remove }) => {
                return (
                  <>
                    <div className="inputBody">
                      <label></label>
                      <div style={{ textAlign: "right" }}>
                        {hasUnsavedItem && (
                          <Button
                            className="cancelBtn"
                            onClick={handleClear}
                          >
                            Clear
                          </Button>
                        )}
                        <Button
                          className="greenBtn"
                          onClick={handleAddMore}
                        >
                          Add <PlusOutlined />
                        </Button>
                      </div>
                    </div>
                    {fields.map(
                      ({ key, name, ...restField }, i) => {
                        return (
                          <div className="card-item">
                            <Col
                              className="gutter-row tabContent"
                              span={16}
                              style={{ paddingRight: "25px" }}
                            >
                              <div className="d-flex">
                                <Form.Item
                                  label="Name/Title"
                                  className="w-100"
                                  {...restField}
                                  labelCol={{
                                    span: window.width > 1024 ? 4 : 6,
                                  }}
                                  name={[name, "title"]}
                                  rules={[
                                    {
                                      required: true,
                                      message: "Article title is required",
                                    },
                                  ]}
                                >
                                  <Input
                                    placeholder="Enter article title"
                                    maxLength={40}
                                    showCount={{
                                      formatter: ({ count }) => `${count}/40`,
                                    }}
                                    
                                    onKeyDown={(e) => {
                                      const currentValue =
                                        form.getFieldValue([whichTab, name, "title"]) || "";

                                      if (
                                        e.key.length === 1 && // normal characters only
                                        currentValue.length >= 40
                                      ) {
                                        form.setFields([
                                          {
                                            name: [whichTab, name, "title"],
                                            errors: ["Title cannot exceed 40 characters"],
                                          },
                                        ]);
                                      }
                                    }}
                                    onBlur={() => {
                                      form.setFields([
                                        {
                                          name: [whichTab, name, "title"],
                                          errors: [],
                                        },
                                      ]);
                                    }}
                                  />
                                </Form.Item>

                                <div
                                  className="remove-btn"
                                  style={{
                                    position: "relative",
                                  }}
                                >
                                  <DeleteOutlined
                                    title="Delete News"
                                    onClick={() => {
                                      const id =
                                        form.getFieldValue([
                                          whichTab,
                                          i,
                                          "id",
                                        ]);
                                      if (id) {
                                        // Existing saved record → call delete API
                                        deleteSingleItem(
                                          whichTab,
                                          id,
                                          form
                                        );
                                      } else {
                                        // Newly added unsaved record → just remove from form list
                                        remove(name);
                                        setHasUnsavedItem(
                                          false
                                        );
                                        setTimeout(
                                          refreshUnsavedState,
                                          0
                                        );
                                      }
                                    }}
                                  />
                                </div>
                              </div>
                              <Form.Item
                                label="Description"
                                {...restField}
                                labelCol={{
                                  span:
                                    window.width > 1024 ? 4 : 6,
                                }}
                                name={[name, "description"]}
                                rules={[
                                  {
                                    required: true,
                                    message:
                                      "Article description is required",
                                    validator: (_, value) => {
                                      const description =
                                        form.getFieldValue(
                                          whichTab
                                        )[i]?.description || "";
                                      if (
                                        !description ||
                                        description.trim() ===
                                        "<p><br></p>"
                                      ) {
                                        return Promise.reject(
                                          new Error(
                                            "Blog description is required"
                                          )
                                        );
                                      }
                                      return Promise.resolve();
                                    },
                                  },
                                ]}
                                validateStatus={
                                  !form.getFieldValue(whichTab)[
                                    i
                                  ]?.description ||
                                    form
                                      .getFieldValue(whichTab)
                                    [i]?.description.trim() ===
                                    "<p><br></p>"
                                    ? "error"
                                    : "success"
                                }
                              >
                                <ReactQuill
                                  value={
                                    form.getFieldValue(
                                      whichTab
                                    )[i]?.description || ""
                                  }
                                  onChange={(value) =>
                                    handleDescriptionChange(
                                      value,
                                      i
                                    )
                                  }
                                  modules={ReactQuillModules}
                                />
                              </Form.Item>
                              <div className="d-flex">
                                <Form.Item
                                  {...restField}
                                  className="w-100"
                                  name={[
                                    name,
                                    "mediaLink",
                                    "mediaType",
                                  ]}
                                  label="Video or Image"
                                  labelCol={{
                                    span:
                                      window.width > 1024
                                        ? 4
                                        : 6,
                                  }}
                                >
                                  <Select
                                    name={[
                                      name,
                                      "mediaLink",
                                      "mediaType",
                                    ]}
                                    onChange={(e) =>
                                      SelectChangeHandler(e, i)
                                    }
                                    placeholder="Select Option"
                                  >
                                    <Select.Option value="VIMEO_MEDIA">
                                      Add Vimeo Link
                                    </Select.Option>
                                    <Select.Option value="SOCIAL_MEDIA">
                                      Add Youtube Link
                                    </Select.Option>
                                    <Select.Option value="SYNTHESIA_MEDIA">
                                      Add Synthesia Link
                                    </Select.Option>
                                    <Select.Option value="IMAGE">
                                      Upload Image
                                    </Select.Option>
                                  </Select>
                                </Form.Item>
                                <Form.Item>
                                  <div className="remove-btn">
                                    <Tooltip
                                      title={
                                        "Upload Video link or image with size lesser than 2.5MB. For best results, use images and videos with a 16:9 aspect ratio"
                                      }
                                    >
                                      <QuestionCircleOutlined />
                                    </Tooltip>
                                  </div>
                                </Form.Item>
                              </div>
                              <div className="d-flex">
                                <Form.Item
                                  label="Article external Link"
                                  className="w-100"
                                  {...restField}
                                  labelCol={{
                                    span:
                                      window.width > 1024
                                        ? 4
                                        : 6,
                                  }}
                                  name={[name, "urlLink"]}
                                  rules={[
                                    {
                                      type: "url",
                                      message:
                                        "Please enter a valid URL",
                                    },
                                  ]}
                                >
                                  <Input placeholder="Article external Link" />
                                </Form.Item>
                                {tabData[whichTab]?.[i]
                                  ?.urlLink && (
                                    <Form.Item>
                                      <div>
                                        <a
                                          className="remove-btn"
                                          target="_blank"
                                          href={
                                            tabData[whichTab]?.[i]
                                              .urlLink
                                          }
                                        >
                                          <Tooltip
                                            title={
                                              "External link"
                                            }
                                          >
                                            <LinkOutlined />
                                          </Tooltip>
                                        </a>
                                      </div>
                                    </Form.Item>
                                  )}
                              </div>
                              {findValue(i, "type") ===
                                "SOCIAL_MEDIA" ? (
                                <Form.Item
                                  label="Youtube Link"
                                  {...restField}
                                  labelCol={{
                                    span:
                                      window.width > 1024
                                        ? 4
                                        : 6,
                                  }}
                                  name={[
                                    name,
                                    "mediaLink",
                                    "url",
                                  ]}
                                >
                                  <Input />
                                </Form.Item>
                              ) : findValue(i, "type") ===
                                "VIMEO_MEDIA" ? (
                                <Form.Item
                                  label="Vimeo Link"
                                  labelCol={{
                                    span:
                                      window.width > 1024
                                        ? 4
                                        : 6,
                                  }}
                                  {...restField}
                                  name={[
                                    name,
                                    "mediaLink",
                                    "url",
                                  ]}
                                >
                                  <Input />
                                </Form.Item>
                              ) : findValue(i, "type") ===
                                "IMAGE" ? (
                                <Form.Item
                                  {...restField}
                                  label="Image"
                                  colon={false}
                                  name="media"
                                  className="experienceImg"
                                  labelCol={{
                                    span:
                                      window.width > 1024
                                        ? 4
                                        : 6,
                                  }}
                                >
                                  {findValue(i, "blob") ? (
                                    <Avatar
                                      src={URL.createObjectURL(
                                        findValue(i, "blob")
                                      )}
                                      alt=""
                                    />
                                  ) : findValue(i, "url") ? (
                                    <Avatar
                                      src={findValue(i, "url")}
                                      alt=""
                                    />
                                  ) : (
                                    <Avatar src={UserIcon} />
                                  )}

                                  <label
                                    name="file"
                                    className="fileUploadBtn greenBtn"
                                  >
                                    {" "}
                                    Choose File
                                    <input
                                      accept="image/png, image/gif, image/jpeg"
                                      type="file"
                                      onChange={(e) => {
                                        updateImage(
                                          e,
                                          i,
                                          form,
                                          whichTab
                                        );
                                        // 🔁 Force re-render after image upload
                                        setTimeout(
                                          () =>
                                            setRefreshKey(
                                              (prev) => prev + 1
                                            ),
                                          200
                                        );
                                      }}
                                    />
                                  </label>
                                </Form.Item>
                              ) : findValue(i, "type") ===
                                "SYNTHESIA_MEDIA" ? (
                                <Form.Item
                                  label="Synthesia Link"
                                  labelCol={{ span: 6 }}
                                  {...restField}
                                  name={[
                                    name,
                                    "mediaLink",
                                    "url",
                                  ]}

                                >
                                  <Input />
                                </Form.Item>
                              ) : null}

                              <Form.Item
                                className="w-100"
                                style={{
                                  display: "flex",
                                  justifyContent: "end",
                                }}
                              >
                                {formChanged ? (
                                  <Button
                                    className="saveBtn greenBtn"
                                    onClick={() => {
                                      form.submit();
                                      setCanAddNew(true);
                                      setHasUnsavedItem(false);
                                    }}
                                  >
                                    Save
                                  </Button>
                                ) : (
                                  <Button
                                    className="saveBtn greenBtn"
                                    style={{
                                      opacity: "0.5",
                                      pointerEvents: "none",
                                    }}
                                  >
                                    Save
                                  </Button>
                                )}
                              </Form.Item>
                            </Col>
                            <Col
                              className="gutter-row tabContent"
                              span={8}
                            >
                              {form.getFieldValue(whichTab)?.[
                                i
                              ] ? (
                                <div key={refreshKey}>
                                  {renderMedia(
                                    form.getFieldValue(
                                      whichTab
                                    )[i]
                                  )}
                                </div>
                              ) : null}
                            </Col>
                          </div>
                        );
                      }
                    )}
                  </>
                );
              }

              }
            </Form.List>
          </Col>
        ) : (
          <></>
        )
        }
      </Row>
    </div>
  }
}

export default NewsTab